import React, { useCallback, useEffect, useState } from "react";
import qs from "qs";
import {
  Badge,
  BadgeGroup,
  Button,
  Card,
  CellText,
  EmptyState,
  Modal,
  PageHeader,
  Pagination,
  Plus,
  SearchLg,
  Send01,
  Select,
  Table,
  TextInput,
  Upload01,
  Users01,
} from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import StaffingTabs from "./StaffingTabs";
import PersonDrawer from "./PersonDrawer";
import PeopleImportModal from "./PeopleImportModal";
import { canImportRoster } from "./staffingScopes";
import PositionBadges from "./PositionBadges";
import { useStaffingPeopleContext } from "./StaffingPeopleContext";
import { errorMessages, formatPhone, invitationSummary, PORTAL_STATUS_COLORS, portalStatusLabel } from "./staffingFormatters";
import { hoursLabel } from "./scheduleFormatters";

const FILTER_KEYS = {
  venue: "staff_member_venues_venue_ownership_id_eq",
  position: "staff_member_positions_staff_position_id_eq",
  department: "staff_positions_staff_department_id_eq",
  status: "status_eq",
};

const DEFAULT_FILTERS = { venue: "", position: "", department: "", status: "active" };

const PeopleRoster = () => {
  const { tabPaths, permissions, permissionScopes, filterOptions, paths } = useStaffingPeopleContext();
  const canImport = canImportRoster(permissions, permissionScopes);
  const [people, setPeople] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [uninvitedIds, setUninvitedIds] = useState([]);
  const [confirmingInviteAll, setConfirmingInviteAll] = useState(false);

  const hasFilters = search.trim() !== "" || Object.entries(filters).some(([key, value]) => value && !(key === "status" && value === "active"));

  useEffect(() => {
    let ignore = false;
    const q = { first_name_or_last_name_cont: search.trim() || undefined };
    Object.entries(filters).forEach(([key, value]) => {
      if (value) q[FILTER_KEYS[key]] = value;
    });
    setLoading(true);
    client
      .get(`${paths.index}?${qs.stringify({ q, page }, { arrayFormat: "brackets" })}`)
      .then(({ data }) => {
        if (ignore) return;
        setPeople(data.staffMembers);
        setPagination(data.pagination);
        setUninvitedIds(data.uninvitedIds || []);
      })
      .catch(() => {
        if (!ignore) toastError("Could not load your people.");
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [paths.index, search, filters, page, reloadKey]);

  useEffect(() => setSelectedIds([]), [search, filters, page]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  const invite = (staffMemberIds) => {
    setInviting(true);
    client
      .post(paths.invitations, { staff_member_ids: staffMemberIds })
      .then(({ data }) => {
        const { message, skippedMessage } = invitationSummary(data);
        if (data.invited_ids.length > 0) toastSuccess(message);
        if (skippedMessage) toastError(skippedMessage);
        setSelectedIds((ids) => ids.filter((id) => !data.invited_ids.includes(id)));
        reload();
      })
      .catch((error) => toastError(errorMessages(error)[0] || "Could not send invites."))
      .finally(() => setInviting(false));
  };

  const setFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value ?? "" }));
  };

  const clearFilters = () => {
    setPage(1);
    setSearch("");
    setFilters(DEFAULT_FILTERS);
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      primary: true,
      render: (person) => (
        <CellText
          primary={
            <a href={paths.staffMember.replace(":id", person.id)} className="text-reset">
              {person.full_name}
            </a>
          }
          supporting={person.email || undefined}
        />
      ),
    },
    { key: "positions", header: "Positions", render: (person) => <PositionBadges positions={person.positions} /> },
    {
      key: "venues",
      header: "Venues",
      render: (person) => (person.venues.length ? person.venues.map((venue) => venue.name).join(", ") : "—"),
    },
    permissions.readContactDetails && { key: "phone", header: "Phone", render: (person) => formatPhone(person.phone) || "—" },
    {
      key: "status",
      header: "Status",
      render: (person) => (
        <BadgeGroup>
          <Badge color={person.status === "active" ? "success" : "gray"} size="sm">
            {person.status === "active" ? "Active" : "Archived"}
          </Badge>
          {person.pin_shared && (
            <Badge color="warning" size="sm" title="Someone else on the team has this PIN, so it finds no one at the kiosk. Reset it.">
              Shared PIN
            </Badge>
          )}
        </BadgeGroup>
      ),
    },
    {
      key: "portal",
      header: "App",
      render: (person) => (
        <Badge color={PORTAL_STATUS_COLORS[person.portal_status]} size="sm">
          {portalStatusLabel(person)}
        </Badge>
      ),
    },
    {
      key: "week",
      header: "This week",
      align: "right",
      render: (person) => (person.week_minutes ? `${hoursLabel(person.week_minutes)} h` : "—"),
    },
    (permissions.manageRoster || permissions.invite) && {
      key: "actions",
      header: "",
      align: "right",
      render: (person) => (
        <div className="d-flex justify-content-end" style={{ gap: 4 }}>
          {permissions.invite && person.can_edit && person.status === "active" && (
            <Button variant="tertiary" size="sm" disabled={inviting} onClick={() => invite([person.id])}>
              {person.portal_status === "not_invited" ? "Invite" : "Re-invite"}
            </Button>
          )}
          {person.can_edit && (
            <Button variant="tertiary" size="sm" onClick={() => setEditing(person)}>
              Edit
            </Button>
          )}
        </div>
      ),
    },
  ].filter(Boolean);

  const emptyState = hasFilters ? (
    <EmptyState
      icon={SearchLg}
      title="No one matches these filters"
      description="Try a different name, venue, position or status."
      secondaryAction={{ label: "Clear filters", onClick: clearFilters }}
    />
  ) : (
    <EmptyState
      icon={Users01}
      title="Add the people who work your events"
      description="Paid staff and volunteers live here together. Add people one at a time or import a spreadsheet, then give each person their positions and venues."
      primaryAction={permissions.manageRoster ? { label: "Add a person", onClick: () => setEditing({}) } : undefined}
      secondaryAction={canImport ? { label: "Import CSV", onClick: () => setImporting(true) } : undefined}
    />
  );

  return (
    <>
      <PageHeader
        title="Staffing"
        supportingText="Everyone who works your events, paid or volunteer."
        actions={
          (permissions.manageRoster || uninvitedIds.length > 0) && (
            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              {permissions.invite && uninvitedIds.length > 0 && (
                <Button variant="secondary" icon={Send01} disabled={inviting} onClick={() => setConfirmingInviteAll(true)}>
                  {`Invite ${uninvitedIds.length} to app`}
                </Button>
              )}
              {canImport && (
                <Button variant="secondary" icon={Upload01} onClick={() => setImporting(true)}>
                  Import CSV
                </Button>
              )}
              {permissions.manageRoster && (
                <Button variant="primary" icon={Plus} onClick={() => setEditing({})}>
                  Add person
                </Button>
              )}
            </div>
          )
        }
      />
      <div className="mt-3">
        <StaffingTabs current="people" paths={tabPaths} />
      </div>

      <Card padding="md">
        <div className="d-flex flex-wrap mb-3" style={{ gap: 12 }}>
          <div style={{ minWidth: 220, flex: "1 1 220px" }}>
            <TextInput
              id="staffing-people-search"
              size="sm"
              leadingIcon={SearchLg}
              placeholder="Search by name"
              value={search}
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
            />
          </div>
          <Select id="staffing-filter-venue" size="sm" width={180} placeholder="All venues" clearable options={filterOptions.venues} value={filters.venue} onChange={(value) => setFilter("venue", value)} />
          <Select id="staffing-filter-department" size="sm" width={200} placeholder="All departments" clearable options={filterOptions.departments} value={filters.department} onChange={(value) => setFilter("department", value)} />
          <Select id="staffing-filter-position" size="sm" width={180} placeholder="All positions" clearable searchable options={filterOptions.positions} value={filters.position} onChange={(value) => setFilter("position", value)} />
          <Select id="staffing-filter-status" size="sm" width={150} placeholder="Any status" clearable options={filterOptions.statuses} value={filters.status} onChange={(value) => setFilter("status", value)} />
        </div>

        {permissions.invite && selectedIds.length > 0 && (
          <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
            <span>{selectedIds.length} selected</span>
            <Button variant="primary" size="sm" icon={Send01} disabled={inviting} onClick={() => invite(selectedIds)}>
              Invite selected
            </Button>
          </div>
        )}

        <Table
          columns={columns}
          data={people}
          loading={loading}
          getRowKey={(person) => person.id}
          stackBelow={720}
          empty={emptyState}
          selectable={permissions.invite}
          selectedKeys={selectedIds}
          onSelectionChange={setSelectedIds}
        />

        {pagination && pagination.total_pages > 1 && (
          <div className="mt-3">
            <Pagination currentPage={pagination.current_page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}
      </Card>

      {editing && <PersonDrawer person={editing} onClose={() => setEditing(null)} onSaved={reload} />}
      {confirmingInviteAll && (
        <Modal
          open
          size="sm"
          title={`Invite ${uninvitedIds.length} to the app?`}
          description="Everyone active who hasn't been invited yet gets a text or email with a link to the staff app, not just the people shown."
          onClose={() => setConfirmingInviteAll(false)}
          footer={
            <div className="d-flex justify-content-end w-100" style={{ gap: 8 }}>
              <Button variant="secondary" onClick={() => setConfirmingInviteAll(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={inviting}
                onClick={() => {
                  setConfirmingInviteAll(false);
                  invite(uninvitedIds);
                }}
              >
                {`Send ${uninvitedIds.length} invites`}
              </Button>
            </div>
          }
        />
      )}
      {importing && <PeopleImportModal onClose={() => setImporting(false)} onImported={reload} />}
    </>
  );
};

export default PeopleRoster;
